import { useCallback } from 'react'
import { useUiStore, type UiLanguage } from '@/stores'
import { en } from './en'
import { hi } from './hi'

/**
 * The interface-language layer.
 *
 * There is no i18n dependency and no locale provider — the whole mechanism is
 * a flat key → string lookup plus the `uiLanguage` value in `useUiStore`, which
 * mirrors the server-side profile preference. `useT` subscribes to that value,
 * so switching language re-renders every consumer without a reload.
 *
 * `t(key, vars)` interpolates `{name}` placeholders; dynamic values (names,
 * counts, dates) are passed as vars and never translated. A missing key falls
 * back to English and then to the key itself, so an untranslated string shows
 * as English rather than blank.
 *
 * `tt(key, vars)` is the non-reactive form for code that runs outside React —
 * toast helpers, query-hook error handlers, `internationalization/format.ts`. It reads the store
 * once at call time, which is correct for a message produced at a single moment.
 */

type Vars = Record<string, string | number>
type Dict = Record<string, string>

const RESOURCES: Record<UiLanguage, Dict> = { en, hi }

function interpolate(template: string, vars?: Vars): string {
  if (!vars) return template
  return template.replace(/\{(\w+)\}/g, (_, key: string) =>
    key in vars ? String(vars[key]) : `{${key}}`,
  )
}

/** Sets `<html lang>`. Called by Providers alongside the theme effect. */
export function applyUiLanguage(language: UiLanguage) {
  document.documentElement.lang = language
}

export function translate(language: UiLanguage, key: string, vars?: Vars): string {
  const raw = RESOURCES[language][key] ?? en[key] ?? key
  return interpolate(raw, vars)
}

/**
 * Non-reactive translate, for code that runs outside a React render — toast
 * helpers, query-hook error handlers, `internationalization/format.ts`. Reads the store once.
 */
export function tt(key: string, vars?: Vars): string {
  return translate(useUiStore.getState().uiLanguage, key, vars)
}

/**
 * `const t = useT()` then `t('nav.item.meetings')` or `t('greeting', { name })`.
 * Re-renders its component when the interface language changes; stable per
 * language so it is safe in hook dependency arrays.
 */
export function useT() {
  const language = useUiStore((s) => s.uiLanguage)
  return useCallback((key: string, vars?: Vars) => translate(language, key, vars), [language])
}
